import { supabase } from "@/integrations/supabase/client";

const VISITOR_KEY = "inq_visitor_id";
const CODE_KEY = "inq_aff_code";
const CLICK_KEY = "inq_aff_click_at";
const CODE_RE = /^[a-zA-Z0-9_-]{3,40}$/;

function newVisitorId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

/** Stable anonymous visitor id used to tie a click to a later signup. */
export function getVisitorId(): string {
  if (typeof window === "undefined") return "";
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = newVisitorId();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

/**
 * Records an affiliate click when the visitor lands with ?ref= / ?via= / ?coupon=.
 * The code is remembered locally so attribution survives navigation before signup.
 */
export async function trackAffiliateVisit(): Promise<void> {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("ref") ?? params.get("via") ?? params.get("aff");
  const coupon = params.get("coupon");
  if (!raw || !CODE_RE.test(raw)) return;

  const code = raw.toLowerCase();
  const visitorId = getVisitorId();
  const lastAt = Number(localStorage.getItem(CLICK_KEY) ?? 0);
  localStorage.setItem(CODE_KEY, code);

  // De-dupe rapid re-renders / refreshes within 5 minutes.
  if (localStorage.getItem(CODE_KEY) === code && Date.now() - lastAt < 5 * 60_000) return;
  localStorage.setItem(CLICK_KEY, String(Date.now()));

  try {
    await supabase.rpc("record_affiliate_click" as never, {
      _code: code,
      _visitor_id: visitorId,
      _landing_page: window.location.pathname,
      _referrer: document.referrer || undefined,
      _source: params.get("utm_source") ?? undefined,
      _medium: params.get("utm_medium") ?? undefined,
      _campaign: params.get("utm_campaign") ?? undefined,
      _coupon_code: coupon && CODE_RE.test(coupon) ? coupon : undefined,
      _redirect_to: window.location.pathname,
    } as never);
  } catch {
    /* attribution must never break the page */
  }
}

/** After sign-in/up, bind the stored click to the account (server blocks self-referral). */
export async function bindAffiliateAttribution(): Promise<void> {
  if (typeof window === "undefined") return;
  const code = localStorage.getItem(CODE_KEY);
  const visitorId = localStorage.getItem(VISITOR_KEY);
  if (!code || !visitorId) return;
  try {
    const { data, error } = await supabase.rpc("bind_affiliate_attribution" as never, {
      _visitor_id: visitorId,
    } as never);
    const res = data as { ok?: boolean } | null;
    if (!error && res?.ok !== false) localStorage.removeItem(CODE_KEY);
  } catch {
    /* noop */
  }
}
